// src/hooks/useNestihaVyjezd.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Odpoved } from '../api/JsdhNotifikaceService/generated';
import { useDojezdNastaveni } from './useDojezdNastaveni';

type OdpovedDojezdu = Pick<Odpoved, 'odpoved' | 'dojezdCas'>;

/**
 * Hasic nestihne limit vyjezdu, kdyz jeho ohlaseny prijezd do zbrojnice padne az za
 * `casVyhlaseni + casVyjezdMinuty`. Presne na limitu jeste stiha.
 *
 * Oranzova trva jen do ohlaseneho prijezdu: jakmile hasici dobehne jeho vlastni odpocet
 * (`now >= dojezdCas`), zezelena jako ostatni.
 */
export function nestihaVyjezd(
    dojezdCas: string | undefined,
    casVyhlaseni: string | undefined,
    casVyjezdMinuty: number,
    now: number,
): boolean {
    if (!dojezdCas || !casVyhlaseni) return false;
    const dojezd = Date.parse(dojezdCas);
    // Nevalidni retezec da NaN a porovnani s NaN je vzdy false — zadny pad, zadna oranzova.
    return dojezd > Date.parse(casVyhlaseni) + casVyjezdMinuty * 60_000 && now < dojezd;
}

/**
 * Vraci predikat pro jednotlive odpovedi. Limit a prepinac se ctou jednou za komponentu,
 * predikat jde pak volat i uvnitr .map() (kde hook volat nejde).
 *
 * `odpovedi` slouzi jen k naplanovani prekresleni: hook si pohlida nejblizsi ohlaseny
 * prijezd oranzoveho hasice a v ten okamzik komponentu prekresli, aby zezelenala.
 * Zamerne ne sdilene sekundove hodiny — ty by cele seznamy odpovedi na RPi prekreslovaly
 * kazdou sekundu, i kdyz se barva meni jen par okamziku za poplach.
 */
export const useNestihaVyjezd = (casVyhlaseni: string | undefined, odpovedi: readonly OdpovedDojezdu[]) => {
    const { casVyjezdMinuty, odpocetZapnut: zapnuto } = useDojezdNastaveni();
    const [tik, setTik] = useState(0);

    const jeNestihajici = useCallback(
        (odpoved: OdpovedDojezdu) =>
            zapnuto && odpoved.odpoved === 'JEDU'
            && nestihaVyjezd(odpoved.dojezdCas, casVyhlaseni, casVyjezdMinuty, Date.now()),
        // `tik` neni v tele potreba, ale nova identita predikatu po zezelenani zajisti,
        // ze ho prepocitaji i memoizovane podkomponenty.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [zapnuto, casVyhlaseni, casVyjezdMinuty, tik],
    );

    // Nejblizsi budouci prijezd hasice, ktery je prave oranzovy.
    const dalsiZezelenani = useMemo(() => {
        let nejblizsi: number | undefined;
        for (const o of odpovedi) {
            if (!jeNestihajici(o)) continue;
            const prijezd = Date.parse(o.dojezdCas!);
            if (nejblizsi === undefined || prijezd < nejblizsi) nejblizsi = prijezd;
        }
        return nejblizsi;
    }, [odpovedi, jeNestihajici]);

    useEffect(() => {
        if (dalsiZezelenani === undefined) return;
        const id = setTimeout(() => setTik((t) => t + 1), Math.max(0, dalsiZezelenani - Date.now()));
        return () => clearTimeout(id);
    }, [dalsiZezelenani]);

    return jeNestihajici;
};
